<!-- https://loterij.atlassian.net/wiki/spaces/NLDT/pages/3575349319/Markdown -->
<!-- eslint-disable vue/no-v-html -->
<template>
	<div
		:class="[$style.markdown, textAlign && $style[textAlign], type && $style[type], surfaceType === 'message' && $style.asMessage]"
		:data-test="dataTest"
		v-html="renderedMarkdown"
	/>
</template>

<script lang="ts" setup>
import md from 'markdown-it';

type Props = {
	markdown: string;
	dataTest?: string;
	textAlign?: 'left' | 'right' | 'center';
	type?: 'body' | 'additionalInfo' | 'small';
	surfaceType?: 'surface' | 'message';
};

const props = withDefaults(defineProps<Props>(), {
	dataTest: 'markdown',
	textAlign: undefined,
	type: 'body',
	surfaceType: 'surface',
});

const markdownRenderer = md({ html: true });

const renderedMarkdown = computed((): string => {
	if (!props.markdown) {
		return '';
	}

	return markdownRenderer.render(props.markdown);
});
</script>

<style lang="scss" module>
.markdown {
	> * {
		margin: 0;
		margin-bottom: var(--sizing--m);
		line-height: var(--typography--body-line-height);
		font-size: var(--typography--regular);

		&:last-child {
			margin-bottom: 0;
		}
	}

	a {
		color: var(--link--link);
		font-weight: var(--typography--semibold);
	}

	h1,
	h2,
	h3,
	h4,
	h5 {
		margin: 0;
		color: var(--surface--on-surface);
		font-family: var(--typography--font-family-heading);
		font-weight: var(--typography--bold);
	}

	h1 {
		font-size: var(--typography--h1-size);
		line-height: var(--typography--h1-line-height);
		text-transform: var(--common-heading-h1-text-transform, none);
	}

	h2 {
		font-size: var(--typography--h2-size);
		line-height: var(--typography--h2-line-height);
		text-transform: var(--common-heading-h2-text-transform, none);
	}

	h3 {
		font-size: var(--typography--h3-size);
		line-height: var(--typography--h3-line-height);
		text-transform: var(--common-heading-h3-text-transform, none);
	}

	h4 {
		font-size: var(--typography--h4-size);
		line-height: var(--typography--h4-line-height);
		text-transform: var(--common-heading-h4-text-transform, none);
	}

	h5 {
		font-size: var(--typography--body-size);
		line-height: var(--typography--body-line-height);
		font-family: var(--typography--font-family-body);
	}

	p {
		color: var(--surface--on-surface);
		font-family: var(--typography--font-family-body);
		line-height: var(--typography--body-line-height);

		strong {
			font-weight: var(--typography--bold);
		}
	}

	&.body p {
		font-size: var(--typography--body-size);
	}

	&.additionalInfo p {
		font-size: var(--typography--additional-info-size);
		line-height: var(--typography--additional-info-line-height);
		color: var(--surface--on-surface-dim);
	}

	&.small p {
		font-size: var(--typography--small-size);
		line-height: var(--typography--small-line-height);
	}

	ul,
	ol {
		// bullets are outside of the container. list-style-position: inside let's text wrap under the bullet
		padding-left: 1em;
	}
}

.markdown.asMessage {
	h1,
	h2,
	h3,
	h4,
	h5,
	p,
	&.additionalInfo p {
		color: var(--message--on-message-dim);
	}
}

.left {
	text-align: left;
}

.right {
	text-align: right;
}

.center {
	text-align: center;
}
</style>
